import { voronoi } from '@turf/voronoi';
import { intersect } from '@turf/intersect';
import { convex } from '@turf/convex';
import { featureCollection, multiPolygon, point } from '@turf/helpers';
import type { BBox, Feature, MultiPolygon, Polygon, Position } from 'geojson';

/**
 * Voronoi cell of every hole of a blast, in the site projection (metres).
 *
 * The diagram is built with Turf (`@turf/voronoi`) over the planned collar positions and cut
 * (`@turf/intersect`) to the blast contour when one is known. Without contour the cells are cut
 * to the convex hull of the holes (`@turf/convex`) pushed outwards by half the typical hole
 * spacing, so boundary cells stay about one burden wide instead of running to a bounding box.
 * Consumers: the map (Geology view cells) and the Quality view (powder factor = explosive /
 * (cell area × hole length)).
 *
 * Coordinates must be projected (metric): the diagram is planar and areas are shoelace areas.
 */

export interface HoleCellSite {
  /** Hole publicGuid */
  id: string;
  x:  number;
  y:  number;
}

export interface HoleCellPolygon {
  /** Hole publicGuid */
  id:       string;
  /** One closed exterior ring per piece of the cell (a cell cut by a concave contour may be split). */
  rings:    Position[][];
  /** Planar area in m² (sum of the pieces). */
  area:     number;
}

/** Without a blast contour, the hull of the holes is pushed outwards by this share of the median hole spacing. */
const HULL_OFFSET_SPACING_RATIO = 0.5;
/** Without a blast contour nor a usable hull (collinear holes), cells extend this far beyond the holes' box. */
const FALLBACK_PADDING_RATIO = 0.25;
const FALLBACK_PADDING_MIN_M = 5;

/**
 * @param sites    one entry per hole, in the site projection
 * @param contour  exterior rings of the blast contour in the same projection (closed or not), or null
 */
export function computeHoleCells(sites: readonly HoleCellSite[], contour: readonly Position[][] | null): ReadonlyMap<string, HoleCellPolygon> {
  const result = new Map<string, HoleCellPolygon>();

  // Holes far away from the cluster (bad coordinates) would stretch every boundary cell.
  const kept = dropOutliers(sites);
  // d3's Voronoi (used by Turf) yields no polygon for coincident points: holes sharing a collar share a cell.
  const unique = new Map<string, HoleCellSite[]>();
  for (const site of kept) {
    const key = `${site.x}|${site.y}`;
    const group = unique.get(key);
    if (group) group.push(site); else unique.set(key, [site]);
  }
  const representatives = [...unique.values()].map(group => group[0]);
  if (representatives.length < 2) return result;

  const points = representatives.map(site => [site.x, site.y] as Position);
  let rings = (contour ?? []).map(openRing).filter(ring => ring.length >= 3);
  if (rings.length && !contourIsPlausible(points, rings)) {
    console.warn('HoleCells: contour ignored, its extent does not match the holes', { holes: bbox(points), contour: bbox(rings.flat()) });
    rings = [];
  }
  if (!rings.length) {
    const hull = offsetHull(points, medianSpacing(points) * HULL_OFFSET_SPACING_RATIO);
    if (hull) rings = [hull];
  }

  const bounds = rings.length ? contourBounds(points, rings) : paddedBounds(points);
  const diagram = voronoi(featureCollection(representatives.map(site => point([site.x, site.y]))), { bbox: bounds });
  const mask = rings.length ? multiPolygon(rings.map(ring => [closeRing(ring)])) : null;

  diagram.features.forEach((cell, index) => {
    if (!cell) return;
    const clipped: Feature<Polygon | MultiPolygon> | null = mask ? intersect(featureCollection<Polygon | MultiPolygon>([cell, mask])) : cell;
    if (!clipped) return;

    const pieces = clipped.geometry.type === 'Polygon'
      ? [clipped.geometry.coordinates[0]]
      : clipped.geometry.coordinates.map(polygon => polygon[0]);
    const cellRings = pieces.filter(ring => ring.length >= 4);
    if (!cellRings.length) return;

    const area = cellRings.reduce((sum, ring) => sum + Math.abs(signedArea(ring)), 0);
    for (const site of unique.get(`${representatives[index].x}|${representatives[index].y}`) ?? []) {
      result.set(site.id, { id: site.id, rings: cellRings, area });
    }
  });

  return result;
}

// ── Geometry helpers ───────────────────────────────────────────────────────────

/** Robust outlier filter: drop holes farther from the median hole than 8× the median spread (+20 m). */
function dropOutliers(sites: readonly HoleCellSite[]): HoleCellSite[] {
  if (sites.length < 4) return [...sites];
  const xs = sites.map(s => s.x).sort((a, b) => a - b);
  const ys = sites.map(s => s.y).sort((a, b) => a - b);
  const medianX = xs[Math.floor(xs.length / 2)];
  const medianY = ys[Math.floor(ys.length / 2)];
  const distances = sites.map(s => Math.hypot(s.x - medianX, s.y - medianY));
  const medianDistance = [...distances].sort((a, b) => a - b)[Math.floor(distances.length / 2)];
  const limit = medianDistance * 8 + 20;
  const kept = sites.filter((_, i) => distances[i] <= limit);
  if (kept.length !== sites.length) {
    console.warn(`HoleCells: ${sites.length - kept.length} hole(s) ignored as position outliers`);
  }
  return kept;
}

/** The contour must contain the holes' box and stay within a sane multiple of its size. */
function contourIsPlausible(points: readonly Position[], rings: readonly Position[][]): boolean {
  const holes   = bbox(points);
  const contour = bbox(rings.flat());
  const holesSize   = Math.max(holes[2] - holes[0], holes[3] - holes[1], 1);
  const contourSize = Math.max(contour[2] - contour[0], contour[3] - contour[1]);
  const margin = holesSize * 0.5 + 10;
  const contains =
    contour[0] <= holes[0] + margin && contour[1] <= holes[1] + margin &&
    contour[2] >= holes[2] - margin && contour[3] >= holes[3] - margin;
  return contains && contourSize <= holesSize * 4 + 50;
}

function bbox(points: readonly Position[]): BBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

function buffer([minX, minY, maxX, maxY]: BBox, distance: number): BBox {
  return [minX - distance, minY - distance, maxX + distance, maxY + distance];
}

function paddedBounds(points: readonly Position[]): BBox {
  const extent = bbox(points);
  const size = Math.max(extent[2] - extent[0], extent[3] - extent[1]);
  return buffer(extent, Math.max(FALLBACK_PADDING_MIN_M, size * FALLBACK_PADDING_RATIO));
}

/** Box covering both the holes and the contour, so no cell is cut short before the contour is applied. */
function contourBounds(points: readonly Position[], rings: readonly Position[][]): BBox {
  return buffer(bbox([...points, ...rings.flat()]), 1);
}

/** Median distance from a hole to its nearest neighbour (the drilling pattern spacing, roughly). */
function medianSpacing(points: readonly Position[]): number {
  const nearest = points.map((p, i) => {
    let best = Infinity;
    for (let j = 0; j < points.length; j++) {
      if (j === i) continue;
      const d = Math.hypot(points[j][0] - p[0], points[j][1] - p[1]);
      if (d > 0 && d < best) best = d;
    }
    return best;
  }).filter(Number.isFinite).sort((a, b) => a - b);
  return nearest.length ? nearest[Math.floor(nearest.length / 2)] : 0;
}

/**
 * Convex hull of the holes pushed outwards by `distance` (each edge is translated along its
 * outward normal, consecutive edges re-intersected). Null when the hull is degenerate.
 */
function offsetHull(points: readonly Position[], distance: number): Position[] | null {
  const hull = convex(featureCollection(points.map(p => point(p))));
  if (!hull) return null;
  const ring = openRing(hull.geometry.coordinates[0]);
  if (ring.length < 3) return null;
  const orientation = Math.sign(signedArea(ring));
  if (!orientation || !(distance > 0)) return orientation ? ring : null;

  // Offset lines: point + normal * distance, one per edge
  const lines = ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length];
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    // Outward normal of edge a→b for a CCW ring is (dy, -dx); flipped for CW
    const nx = ((b[1] - a[1]) / length) * orientation;
    const ny = (-(b[0] - a[0]) / length) * orientation;
    return { a: [a[0] + nx * distance, a[1] + ny * distance] as Position, b: [b[0] + nx * distance, b[1] + ny * distance] as Position };
  });
  const offset: Position[] = [];
  for (let i = 0; i < lines.length; i++) {
    const previous = lines[(i + lines.length - 1) % lines.length];
    const current  = lines[i];
    offset.push(lineIntersection(previous.a, previous.b, current.a, current.b) ?? current.a);
  }
  return offset;
}

/** Intersection of two infinite lines p1→p2 and p3→p4, null when parallel. */
function lineIntersection(p1: Position, p2: Position, p3: Position, p4: Position): Position | null {
  const d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
  const d2x = p4[0] - p3[0], d2y = p4[1] - p3[1];
  const denominator = d1x * d2y - d1y * d2x;
  if (Math.abs(denominator) < 1e-12) return null;
  const t = ((p3[0] - p1[0]) * d2y - (p3[1] - p1[1]) * d2x) / denominator;
  return [p1[0] + t * d1x, p1[1] + t * d1y];
}

function openRing(ring: readonly Position[]): Position[] {
  if (ring.length > 1) {
    const first = ring[0];
    const last  = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring.slice(0, -1);
  }
  return [...ring];
}

function closeRing(ring: readonly Position[]): Position[] {
  return [...ring, ring[0]];
}

/** Shoelace area of a ring (closed or not); sign gives the orientation. */
export function signedArea(ring: readonly Position[]): number {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    area += a[0] * b[1] - b[0] * a[1];
  }
  return area / 2;
}
